import { useCallback, useRef, useState } from 'react';
import { classifyCues, GnCue } from '../services/gnCueService';
import { isLikelyQuestionOrProblem } from '../utils/pasteQuestionDetector';
import { simpleHash } from '../utils/simpleHash';
import type { GnRole } from '../components/gn/GnContext';

const DEBOUNCE_MS = 800;
const MAX_CUES_PER_MINUTE = 6; // rate limit — occasional and helpful, not constant (FR-011)

export interface CueOffer {
  cues: GnCue[];
  position: { top: number; left: number };
}

/**
 * Ambient help-cue observer (spec 046 US2, FR-007/FR-008/FR-011). Watches candidate content
 * deltas (paste, or a caller-supplied region), debounces, classifies server-side, and
 * suppresses a cue for the same content once dismissed for the rest of the session
 * (SC-009) — err on the side of silence for anything that doesn't look actionable.
 */
export function useCueObserver(role: GnRole, courseId: number, nodeId: number | null) {
  const [offer, setOffer] = useState<CueOffer | null>(null);
  const dismissedHashesRef = useRef<Set<string>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recentOffersRef = useRef<number[]>([]);

  const underRateLimit = () => {
    const now = Date.now();
    recentOffersRef.current = recentOffersRef.current.filter(t => now - t < 60_000);
    return recentOffersRef.current.length < MAX_CUES_PER_MINUTE;
  };

  const observeCandidate = useCallback((
    text: string,
    position: { top: number; left: number },
    imageBase64?: string,
  ) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    const trimmed = (text || '').trim();
    const hash = simpleHash(trimmed + (imageBase64 ? ':img' : ''));
    if (dismissedHashesRef.current.has(hash)) return;
    if (!imageBase64 && !isLikelyQuestionOrProblem(trimmed)) return; // err on the side of silence

    debounceTimerRef.current = setTimeout(async () => {
      if (!underRateLimit()) return;
      try {
        const cues = await classifyCues(role, courseId, nodeId, trimmed, hash,
          imageBase64 ? { pastedImageBase64: imageBase64 } : undefined);
        if (cues.length === 0) return;
        if (dismissedHashesRef.current.has(hash)) return; // dismissed while the call was in flight
        recentOffersRef.current.push(Date.now());
        setOffer({ cues, position });
      } catch {
        // classification failure offers no cues — never surfaces as an error to the user
      }
    }, DEBOUNCE_MS);
  }, [role, courseId, nodeId]);

  const dismiss = useCallback(() => {
    if (offer) {
      const hash = offer.cues[0]?.targetHash;
      if (hash) dismissedHashesRef.current.add(hash);
    }
    setOffer(null);
  }, [offer]);

  const clear = useCallback(() => setOffer(null), []);

  return { offer, observeCandidate, dismiss, clear };
}
